/**
 * Paywall (HTTP 402) parsing — one place that understands every code the
 * backend can return, so a gated feature never reads as a broken page.
 *
 * The backend returns 402 with a structured `detail` from four call sites:
 *   - `hc_search_requires_max`  cases.py — High Court full-text is Max-tier
 *   - `feature_not_in_plan`     dependencies.py — feature outside the plan's
 *                               allowlist
 *   - `subscription_required`   dependencies.py — trial expired / revoked
 *   - `{resource}_limit_reached` matter_service.py — slot cap (matters,
 *                               matters_reopen); already handled by
 *                               MatterLimitBanner, included here so a caller
 *                               that hits it outside that banner still gets a
 *                               sensible message rather than a raw axios error
 *
 * Callers get `null` for anything that is not a paywall, so the usual error
 * path stays untouched.
 */

export type PaywallCode =
  | 'hc_search_requires_max'
  | 'feature_not_in_plan'
  | 'subscription_required'
  | 'limit_reached'
  | 'credits_exhausted'
  | 'unknown';

export interface PaywallInfo {
  code: PaywallCode;
  /** Message from the server — already lawyer-facing; prefer it over inventing copy. */
  message: string;
  /** Feature key, when the backend names one (e.g. 'hc_search'). */
  feature?: string;
  /** Vertical whose subscription is implicated — what UpgradeModal needs. */
  vertical?: string;
  /** subscription_required only: what lapsed — a trial (→ Subscribe) or a
   *  paid plan (→ Renew). Absent on older responses. */
  lapsed?: 'trial' | 'plan';
  /** Slot-cap details, when the 402 came from the matter cap. */
  limit?: number;
  current?: number;
}

const FALLBACK_MESSAGE = 'This is part of a paid plan. Upgrade to continue.';

/**
 * Extract paywall info from a caught error. Returns null when the error is
 * not an HTTP 402, so callers can keep their existing failure handling.
 *
 * Defensive by design: the shape is read off `unknown` rather than an axios
 * type, because this is called from `catch` blocks where TypeScript cannot
 * know what was thrown, and a paywall that crashes the handler is worse than
 * the original silent failure.
 */
export function parsePaywallError(error: unknown): PaywallInfo | null {
  const response = (error as { response?: { status?: number; data?: unknown } })?.response;
  if (!response || response.status !== 402) return null;

  // `detailRaw` is where the axios interceptor parks the structured body once
  // it has stringified `detail` for safe rendering (utils/apiError.ts). Fall
  // back to `detail` so this still works on any response that skipped the
  // interceptor (direct fetch, tests).
  const data = response.data as { detail?: unknown; detailRaw?: unknown };
  const detail = data?.detailRaw ?? data?.detail;

  // A 402 whose body is a plain string (or missing) still deserves a paywall
  // UI rather than a console log.
  if (typeof detail !== 'object' || detail === null) {
    return {
      code: 'unknown',
      message: typeof detail === 'string' && detail.trim() ? detail : FALLBACK_MESSAGE,
    };
  }

  const d = detail as Record<string, unknown>;
  const rawCode = typeof d.code === 'string' ? d.code : '';
  const code: PaywallCode =
    rawCode === 'hc_search_requires_max' ||
    rawCode === 'feature_not_in_plan' ||
    rawCode === 'subscription_required' ||
    rawCode === 'credits_exhausted'
      ? rawCode
      : rawCode.endsWith('_limit_reached')
      ? 'limit_reached'
      : 'unknown';

  return {
    code,
    message:
      typeof d.message === 'string' && d.message.trim() ? d.message : FALLBACK_MESSAGE,
    feature: typeof d.feature === 'string' ? d.feature : undefined,
    vertical: typeof d.vertical === 'string' ? d.vertical : undefined,
    lapsed: d.lapsed === 'trial' || d.lapsed === 'plan' ? d.lapsed : undefined,
    limit: typeof d.limit === 'number' ? d.limit : undefined,
    current: typeof d.current === 'number' ? d.current : undefined,
  };
}

/** Short call-to-action label per code. */
export function paywallCtaLabel(code: PaywallCode, lapsed?: PaywallInfo['lapsed']): string {
  switch (code) {
    case 'hc_search_requires_max':
      return 'See Max plan';
    case 'subscription_required':
      // A lawyer whose PAID plan lapsed renews; only a trial "subscribes".
      return lapsed === 'plan' ? 'Renew' : 'Subscribe';
    case 'credits_exhausted':
      // Credits ran out, but the PLAN is still valid — buying more credits
      // is the faster, correct fix (2026-11-02); upgrading the plan does
      // nothing for a lawyer who is simply out of allowance this cycle.
      // See paywallCtaKind below, which both callers branch on to open
      // TopupModal instead of UpgradeModal for this one code.
      return 'Buy credits';
    default:
      return 'Upgrade';
  }
}

/** Which modal the CTA button should open — the single source of truth
 *  both `GlobalPaywallHost` and `PaywallPanel` branch on, so the routing
 *  rule can never drift between the two call sites. */
export function paywallCtaKind(code: PaywallCode): 'topup' | 'upgrade' {
  return code === 'credits_exhausted' ? 'topup' : 'upgrade';
}

/** True for an HTTP 402. A page's generic "failed, try again" path must skip
 *  it: the 402 is a lapsed or limited plan, shown with its fix by the paywall
 *  notice — calling it an outage keeps the lawyer retrying instead of renewing. */
export function isPaywallError(error: unknown): boolean {
  return (error as { response?: { status?: number } })?.response?.status === 402
    || (error as { status?: number })?.status === 402;
}
